// "Put everything back": every experiment of every kind to the allocation its
// declaration carries.
//
// WHY THIS CONTROL EXISTS. An allocation is per environment. `experiment_weights`
// lives in each environment's own Postgres and does NOT travel with a deploy, which
// is the right design and has one sharp edge: after a week of trying things on dev,
// nobody can say which of ninety-six experiments dev is still holding away from the
// configuration prod runs, and the only way back was to remember every number and
// re-type it into whichever of six controls had set it.
//
// It also unsticks the state that made this necessary. `scout.on_site_lock` read
// "off (0%)" on its card while the lock was ON for every on-site brief, because an
// UNALLOCATED logic experiment and one allocated at 0% print the same percentage and
// resolve differently. Moving that dial to 5% then took the lock OFF 95% of briefs:
// the number going up made the product do the thing less. One button that writes the
// declared default is the recovery path for exactly that.
//
// TWO STEPS, and not because of a friction policy. The register's table is a
// worker-built snapshot up to half an hour old, so a confirm step built from it
// would name the wrong experiments. The first press is a DRY RUN against live rows
// and the list you read is the list that will move; the second applies it. The
// reason field is optional, like every other reason on this surface.
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Badge, Button } from "@recruiter/shared";
import type {
  OrphanAllocation,
  RestoreDefaultsResponse,
  RestoredAllocation,
} from "@recruiter/shared";

import { restoreExperimentDefaults } from "../lib/api";
import { adminKeys } from "../lib/queries";
import { errorMessage } from "./states";
import "./restore-defaults.css";

/** An allocation as one readable string: `lock 5%`, or `none` for an empty payload.
 *
 *  Shows EVERY arm the payload names, zeroes included, because "lock 0%" and "none"
 *  are the same behaviour and a reader comparing before with after needs the arm
 *  named on both sides to see that nothing else moved. */
export function formatAllocation(payload: Record<string, number>): string {
  const parts = Object.entries(payload).map(([arm, pct]) => `${arm} ${pct}%`);
  return parts.length > 0 ? parts.join(", ") : "none";
}

/** Is this experiment held away from what its declaration allocates?
 *
 *  Compared over the UNION of both key sets with a missing arm read as zero, the
 *  same rule the server applies: a stored row names only the arms somebody moved, so
 *  `{}` and `{b: 0}` are one allocation spelled two ways. An empty `defaultAllocation`
 *  means a snapshot written before the field existed, which reads as "at the default"
 *  rather than as a page full of drift warnings nobody can act on.
 *
 *  AN EMPTY `allocation` IS "NO ROW", AND NO ROW IS ALWAYS THE DEFAULT. That is the
 *  server's own rule, written out in `admin_experimentation._stored_payload`: an absent
 *  row is not an allocation of zero, it is the kind's no-row fallback, which is the
 *  definition of the default. Only `registry.variant_records` can report an empty
 *  allocation (the `logic` and `config` kinds do when nothing is stored; every other
 *  kind always names its arms), and for a declaration whose default is NOT zero this
 *  answered `true` on an environment that had never touched the experiment.
 *  `scout.on_site_lock` declares `{"lock": 100}` because the lock ships ON, so every
 *  environment with no row read "not at default (lock 100%)" on the board while
 *  `GET /{key}/allocation` reported it AT the default and Restore defaults correctly
 *  found nothing to do. A KILLED row still reads as drift, because a kill writes
 *  `{"lock": 0}` and that is a row. */
export function isOffDefault(
  allocation: Record<string, number>,
  defaultAllocation: Record<string, number>,
): boolean {
  if (Object.keys(defaultAllocation).length === 0) return false;
  if (Object.keys(allocation).length === 0) return false;
  for (const arm of new Set([...Object.keys(allocation), ...Object.keys(defaultAllocation)])) {
    if ((allocation[arm] ?? 0) !== (defaultAllocation[arm] ?? 0)) return true;
  }
  return false;
}

/** The arm the CODE ships, when the declaration ships a non-baseline one.
 *
 *  `"lock 100%"` for `scout.on_site_lock`, `null` for a declaration that ships its
 *  baseline (which is almost all of them, and the silent case).
 *
 *  WHY A SURFACE NEEDS THIS. The register's state ladder describes the EXPERIMENT: an
 *  experiment nobody has allocated reads `off`, which is true and, for one shape of
 *  declaration, badly misleading. `scout.on_site_lock` declares `{"lock": 100}` because
 *  the on-site lock ships ON, and with no allocation row the flag behind it
 *  (`Settings.scout_on_site_lock`) keeps every on-site brief locked. So the board said
 *  `off` about a product that was locking everybody, which is the same sentence, one
 *  surface over, that started this whole line of work: an operator read "off (0%)" on
 *  the dial card and moved the dial believing it was harmless.
 *
 *  "Experiment off" and "feature off" are different claims. This is how the card says
 *  the second one out loud instead of leaving it to be inferred. */
export function shippedArm(defaultAllocation: Record<string, number>): string | null {
  const live = Object.entries(defaultAllocation)
    .filter(([, pct]) => pct > 0)
    .sort(([a], [b]) => a.localeCompare(b));
  if (live.length === 0) return null;
  return live.map(([arm, pct]) => `${arm} ${pct}%`).join(", ");
}

type Props = {
  /** May this operator write? `system_admin` at full. The button is hidden for a
   *  read-only holder; the route refuses them anyway. */
  canAct: boolean;
};

/** Stored rows for experiments the code no longer declares.
 *
 *  Inert, and reported anyway: "nothing to restore" and "the table is clean" are
 *  different claims, and without this the panel made the second one while only the
 *  first was true. Dev carried three on 2026-09-13 (a deleted config experiment and two
 *  renamed slots). Nothing offers to remove them: the table is append-only, so there is
 *  no row to delete. */
function Orphans({ rows }: { rows: readonly OrphanAllocation[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="rsd__orphans">
      <p className="rsd__orphans-what">
        {rows.length} stored {rows.length === 1 ? "row belongs" : "rows belong"} to
        experiments this build no longer declares, probably renamed or deleted. Nothing
        reads them, and the sweep leaves them alone.
      </p>
      <ul className="rsd__list">
        {rows.map((row) => (
          <li className="rsd__row" key={`${row.kind}:${row.key}`}>
            <Badge tone="neutral">{row.kind}</Badge>
            <code className="rsd__key">{row.key}</code>
            <span className="rsd__move">{formatAllocation(row.payload)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RestoreDefaults({ canAct }: Props) {
  const queries = useQueryClient();
  const [plan, setPlan] = useState<RestoreDefaultsResponse | null>(null);
  const [reason, setReason] = useState("");
  const [done, setDone] = useState<RestoreDefaultsResponse | null>(null);

  const preview = useMutation({
    mutationFn: () => restoreExperimentDefaults({ dryRun: true, reason: "", kinds: [] }),
    onSuccess: (answer) => {
      setDone(null);
      setPlan(answer);
    },
  });

  const apply = useMutation({
    mutationFn: () =>
      restoreExperimentDefaults({ dryRun: false, reason: reason.trim(), kinds: [] }),
    onSuccess: (answer) => {
      setPlan(null);
      setReason("");
      setDone(answer);
      void queries.invalidateQueries({ queryKey: adminKeys.experimentation });
    },
  });

  if (!canAct) return null;

  return (
    <div className="rsd">
      <div className="rsd__bar">
        <Button
          size="sm"
          variant="ghost"
          tone="neutral"
          disabled={preview.isPending || apply.isPending}
          onClick={() => preview.mutate()}
        >
          {preview.isPending ? "Checking…" : "Restore defaults"}
        </Button>
        <span className="rsd__hint">
          Puts every experiment back to the allocation its code declares, which is what a
          freshly deployed environment runs. Allocations are per environment, so this never
          reaches another one.
        </span>
      </div>

      {preview.isError ? <p className="rsd__error">{errorMessage(preview.error)}</p> : null}

      {plan ? (
        plan.restored.length === 0 ? (
          <div className="rsd__done" role="status">
            <p className="rsd__ok">
              Nothing to do: all {plan.unchanged} experiments are already at their declared
              defaults.
            </p>
            <Orphans rows={plan.orphans} />
          </div>
        ) : (
          <div className="rsd__panel" role="group" aria-label="Restore these experiments">
            <p className="rsd__what">
              {plan.restored.length} of {plan.restored.length + plan.unchanged} experiments are
              held away from what the code declares. Restoring writes each one back, all in one
              transaction, so it either all lands or none of it does.
            </p>
            <ul className="rsd__list">
              {plan.restored.map((move: RestoredAllocation) => (
                <li className="rsd__row" key={`${move.kind}:${move.key}`}>
                  <Badge tone="neutral">{move.kind}</Badge>
                  <code className="rsd__key">{move.key}</code>
                  <span className="rsd__move">
                    {formatAllocation(move.before)} {"->"} {formatAllocation(move.after)}
                  </span>
                </li>
              ))}
            </ul>
            <input
              className="rsd__reason"
              value={reason}
              placeholder="Why (optional, lands on each audit row)"
              aria-label="Reason for restoring the defaults"
              disabled={apply.isPending}
              onChange={(event) => setReason(event.target.value)}
            />
            {apply.isError ? <p className="rsd__error">{errorMessage(apply.error)}</p> : null}
            <Orphans rows={plan.orphans} />
            <div className="rsd__actions">
              <Button
                size="sm"
                tone="accent"
                disabled={apply.isPending}
                onClick={() => apply.mutate()}
              >
                {apply.isPending
                  ? "Restoring…"
                  : `Restore ${plan.restored.length} experiment${plan.restored.length === 1 ? "" : "s"}`}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                disabled={apply.isPending}
                onClick={() => {
                  setPlan(null);
                  setReason("");
                  apply.reset();
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )
      ) : null}

      {done ? (
        <p className="rsd__ok" role="status">
          Restored {done.restored.length}; {done.unchanged} were already there. The register
          catches up on the worker's next tick.
          {Object.keys(done.failed).length > 0
            ? ` The allocations landed, but the audit trail could not be written for: ${Object.keys(done.failed).join(", ")}.`
            : ""}
        </p>
      ) : null}
    </div>
  );
}
